/**
 * Single shared WebSocket for /api/dm/ws.
 *
 * This app used to open TWO independent DM sockets: one in DmGlobalListener
 * (headless, app-wide, to auto-ack delivery receipts) and another in
 * DirectChatPage (for the chat UI). Both authenticated separately, both
 * registered presence separately, and both received every server broadcast —
 * so a single incoming message was processed twice, and DmGlobalListener sent
 * a duplicate `dm:message:delivered` for each copy, causing duplicate Mongo
 * writes and duplicate status fan-outs back to the sender.
 *
 * One socket, ref-counted across subscribers — the same shape as
 * lib/notifications/notificationRealtime.ts, which this mirrors deliberately
 * so there is one reconnect strategy in this app rather than three.
 */

import { env } from "../../config/env";
import { authStorage } from "../storage/authStorage";

export type DmEvent = { type: string; [key: string]: unknown };
type Listener = (event: DmEvent) => void;

const makeWsUrl = (token: string) => {
  const base = env.apiBaseUrl.replace(/\/+$/, "");
  const wsBase = base.startsWith("https://")
    ? base.replace(/^https:\/\//, "wss://")
    : base.replace(/^http:\/\//, "ws://");
  return `${wsBase}/api/dm/ws?token=${encodeURIComponent(token)}`;
};

/**
 * Full jitter: random() * min(cap, base * 2^attempt).
 *
 * The fixed backoff ladder this replaces meant every client that was connected
 * when a worker restarted reconnected at exactly the same moment — a
 * synchronised stampede aimed at a process that is still warming up. Jitter
 * spreads them uniformly across the window.
 */
const BACKOFF_BASE_MS = 500;
const BACKOFF_CAP_MS = 30_000;
const backoffDelay = (attempt: number) =>
  Math.random() * Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);

class DmSocketHub {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private listeners = new Set<Listener>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refCount = 0;
  private attempt = 0;
  /** Frames requested while the socket was down; flushed on open. */
  private queue: string[] = [];

  constructor() {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible" && this.refCount > 0) {
          this.attempt = 0; // a user-visible tab deserves an immediate retry
          this.ensureConnected();
        }
      });
    }
    if (typeof window !== "undefined") {
      // Neither DM socket previously reacted to the browser coming back
      // online; they waited out the backoff timer instead.
      window.addEventListener("online", () => {
        if (this.refCount > 0) {
          this.attempt = 0;
          this.ensureConnected();
        }
      });
    }
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    this.refCount += 1;
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    this.ensureConnected();
    return () => {
      this.listeners.delete(listener);
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        // Defer by one tick so React StrictMode's cleanup+remount cycle
        // doesn't tear down a socket that is still CONNECTING.
        this.disconnectTimer = setTimeout(() => {
          this.disconnectTimer = null;
          if (this.refCount === 0) this.disconnect();
        }, 0);
      }
    };
  }

  send(payload: Record<string, unknown>): void {
    const data = JSON.stringify(payload);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
      return;
    }
    // Bounded: a long outage must not grow this without limit.
    if (this.queue.length < 50) this.queue.push(data);
  }

  /** Reconnect with the current token — call after login/logout. */
  forceReconnect(): void {
    this.teardown();
    this.attempt = 0;
    if (this.refCount > 0) this.ensureConnected();
  }

  private ensureConnected(): void {
    const token = authStorage.getToken();
    if (!token) return;
    if (
      this.ws &&
      this.token === token &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.teardown();
    this.token = token;
    this.connect(token);
  }

  private connect(token: string): void {
    try {
      const ws = new WebSocket(makeWsUrl(token));
      this.ws = ws;

      ws.onopen = () => {
        this.attempt = 0;
        const pending = this.queue;
        this.queue = [];
        pending.forEach((d) => ws.send(d));
        this.broadcast({ type: "dm:connected" });
      };

      ws.onmessage = (ev) => {
        let event: DmEvent;
        try {
          event = JSON.parse(ev.data as string) as DmEvent;
        } catch {
          return;
        }
        if (event.type === "dm:ping") {
          // Reply immediately so the server can bound its dead-socket
          // detection rather than waiting for a send() to eventually throw.
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "dm:pong" }));
          }
          return;
        }
        this.broadcast(event);
      };

      ws.onclose = () => {
        if (this.token !== token) return; // a newer socket already replaced us
        this.broadcast({ type: "dm:disconnected" });
        this.scheduleReconnect();
      };

      ws.onerror = () => {
        /* onclose drives the reconnect */
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.refCount === 0) return;
    const delay = backoffDelay(this.attempt);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.refCount > 0) this.ensureConnected();
    }, delay);
  }

  private teardown(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const old = this.ws;
    this.ws = null;
    this.token = null;
    if (old) {
      old.onopen = null;
      old.onmessage = null;
      old.onclose = null;
      old.onerror = null;
      try {
        old.close();
      } catch {
        /* ignore */
      }
    }
  }

  private disconnect(): void {
    this.attempt = 0;
    this.queue = [];
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    this.teardown();
  }

  private broadcast(event: DmEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {
        /* one bad handler must not kill the loop */
      }
    }
  }
}

export const dmSocket = new DmSocketHub();
